import React, { createContext, useContext } from 'react';
import { useSnackbar } from 'notistack';

import { Notification } from '../components/app/Notification';
import { SnackBarNotificationOptions } from '../types/SnackBarNotificationOptions';

export interface NotificationContextProps {
  showNotification: (options: SnackBarNotificationOptions) => void;
  closeSnackbar: (key?: string | number) => void;
}

export const NotificationContext =
  createContext<NotificationContextProps | null>(null);

export const NotificationProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const showNotification = (options: SnackBarNotificationOptions) => {
    const showClose = options.showClose ?? true;
    enqueueSnackbar(options.title, {
      variant: options.type,
      anchorOrigin: { horizontal: 'center', vertical: 'top' },
      autoHideDuration: options.autoHideTime ? options.autoHideTime : null,
      preventDuplicate: options.preventDuplicate ?? true,
      content: (key: string | number) => (
        <div>
          <Notification
            data={{
              title: options.title,
              message: options.message ? options.message : '',
              messageType: options.type,
            }}
            onClose={showClose ? () => closeSnackbar(key) : undefined}
          >
            {options.additionalInfo && <>{options.additionalInfo}</>}
          </Notification>
        </div>
      ),
    });
  };

  return (
    <NotificationContext.Provider value={{ showNotification, closeSnackbar }}>
      {children}
    </NotificationContext.Provider>
  );
};

export function useNotification(): NotificationContextProps {
  const context = useContext(NotificationContext);
  return context || assertNever();
}

function assertNever(): never {
  throw Error('Cannot use useNotification outside of NotificationProvider');
}
